import { useMemo } from 'react';
import { CIRCLE_OF_FIFTHS_MAJOR } from '@theory/notes';
import { encodeKeyId, noteToSemitone } from '@util/musicTheory';
import { useAppStore } from '@app/store/useAppStore';
import { useKey } from '@app/hooks/useKey';
import { useHaptics } from '@app/hooks/useHaptics';
import styles from './CircleOfFifthsFull.module.css';

const SEGMENT_COUNT = 12;
const SEGMENT_DEG = 360 / SEGMENT_COUNT;
// Geometry expressed as percentages of viewBox so the diagram scales with its container.
const OUTER_R = 42;
const INNER_R = 27;
const VB = 100;
const CENTER = 50;

const FUNCTION_COLOUR_VAR: Record<string, string> = {
  Tonic: 'var(--colour-tonic)',
  Subdominant: 'var(--colour-subdominant)',
  Dominant: 'var(--colour-dominant)',
  Leading: 'var(--colour-leading)',
};

function polar(r: number, angleDeg: number) {
  const rad = (angleDeg - 90) * (Math.PI / 180);
  return { x: CENTER + r * Math.cos(rad), y: CENTER + r * Math.sin(rad) };
}

// Map pitch-class semitone (0..11) to its index in the Circle of Fifths.
// Handles enharmonics (A# ↔ Bb both → COF index for Bb).
const COF_INDEX_BY_SEMITONE: number[] = (() => {
  const map = new Array(12).fill(-1);
  CIRCLE_OF_FIFTHS_MAJOR.forEach((root, i) => {
    map[noteToSemitone(root)] = i;
  });
  return map;
})();

function cofIndexFromRoot(root: string): number {
  return COF_INDEX_BY_SEMITONE[noteToSemitone(root)] ?? -1;
}

interface Props {
  onSelect?: (keyId: string) => void;
}

// Previously this component read `parent.clientHeight` to cap its own square
// width, but that created a feedback loop in any auto-height container
// (e.g., the mobile shell's section flex column): CoF size affected parent
// height, which affected the cap, which collapsed the CoF to zero.
// Sizing is now pure CSS — see CircleOfFifthsFull.module.css.

export function CircleOfFifthsFull({ onSelect }: Props) {
  const selectedKeyId = useAppStore((s) => s.selectedKeyId);
  const selectKey = useAppStore((s) => s.selectKey);
  const haptics = useHaptics();
  const activeKey = useKey(selectedKeyId);

  const diatonicByIndex = useMemo(() => {
    const map = new Map<number, { romanNumeral: string; chordFunction: string }>();
    if (!activeKey) return map;
    activeKey.diatonicChords.forEach((dc) => {
      const idx = cofIndexFromRoot(dc.rootNote);
      if (idx >= 0) map.set(idx, { romanNumeral: dc.romanNumeral, chordFunction: dc.chordFunction });
    });
    return map;
  }, [activeKey]);

  const handle = (id: string) => {
    haptics.medium();
    selectKey(id);
    onSelect?.(id);
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.inner}>
        <svg
          viewBox={`0 0 ${VB} ${VB}`}
          className={styles.svg}
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
        >
          <circle
            cx={CENTER}
            cy={CENTER}
            r={OUTER_R}
            stroke="var(--colour-border)"
            strokeWidth={0.3}
            fill="none"
          />
          <circle
            cx={CENTER}
            cy={CENTER}
            r={INNER_R}
            stroke="var(--colour-border)"
            strokeWidth={0.3}
            fill="none"
          />

          {Array.from({ length: SEGMENT_COUNT }).map((_, i) => {
            const a = (i * SEGMENT_DEG - SEGMENT_DEG / 2 - 90) * (Math.PI / 180);
            const x1 = CENTER + INNER_R * 0.6 * Math.cos(a);
            const y1 = CENTER + INNER_R * 0.6 * Math.sin(a);
            const x2 = CENTER + OUTER_R * Math.cos(a);
            const y2 = CENTER + OUTER_R * Math.sin(a);
            return (
              <line
                key={`div-${i}`}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="var(--colour-border)"
                strokeOpacity={0.4}
                strokeWidth={0.2}
              />
            );
          })}

        </svg>

        <div className={styles.numerals}>
          {Array.from({ length: SEGMENT_COUNT }).map((_, i) => {
            const entry = diatonicByIndex.get(i);
            if (!entry) return null;
            const innerRingR = INNER_R * 0.62;
            const { x, y } = polar(innerRingR, i * SEGMENT_DEG);
            const colour =
              FUNCTION_COLOUR_VAR[entry.chordFunction] ?? 'var(--colour-text-secondary)';
            return (
              <span
                key={`num-${i}`}
                data-roman-numeral
                className={styles.numeral}
                style={{ left: `${x}%`, top: `${y}%`, color: colour }}
              >
                {entry.romanNumeral}
              </span>
            );
          })}
        </div>

        <div className={styles.buttons}>
          {CIRCLE_OF_FIFTHS_MAJOR.map((root, i) => {
            const majorId = encodeKeyId(root, 'major');
            const pos = polar(OUTER_R, i * SEGMENT_DEG);
            const selected = selectedKeyId === majorId;
            const inMode = diatonicByIndex.has(i);
            return (
              <button
                type="button"
                key={root}
                className={[
                  styles.segBtn,
                  inMode && !selected && styles.inMode,
                  selected && styles.selected,
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{
                  left: `${pos.x}%`,
                  top: `${pos.y}%`,
                  ['--seg-colour' as string]: `var(--colour-keys-${i})`,
                }}
                onClick={() => handle(majorId)}
                aria-label={`${root} major`}
                aria-pressed={selected}
              >
                {root}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
